import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiHelper } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      if (storedUser && storedToken && storedUser !== 'undefined' && storedUser !== 'null') {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && (parsedUser.id || parsedUser.user_id)) {
          setUser(parsedUser);
        } else {
          throw new Error('Invalid user data in storage');
        }
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('orgId');
      }
    } catch (error) {
      console.error('Error initializing auth state:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('orgId');
    } finally {
      setLoading(false);
    }
  }, []);

  // API login function
  const login = async (email, password) => {
    try {
      // Use apiHelper for standardized API calls
      const data = await apiHelper.postResource('/auth/login/', { email: email.toLowerCase(), password });

      // Store tokens
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      if (data.access) {
        localStorage.setItem('token', data.access);
      }
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }

      // Build user data object
      let userData;

      if (data.is_superuser) {
        // Superuser - full access to everything
        userData = {
          id: data.user_id,
          username: data.username,
          email: data.email,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          is_superuser: true,
          is_staff: data.is_staff || false,
          role: null,
          roleDetails: null,
          organization: null,
          orgId: null,
          employeeId: data.employee_id || null,
          permissions: [], // Superuser has all permissions implicitly
          must_reset_password: data.must_reset_password || false
        };
      } else {
        // Regular user with role and organization from API
        const roleObj = data.role || {};
        const orgObj = data.organization || {};

        userData = {
          id: data.user_id,
          username: data.username,
          email: data.email,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          is_superuser: false,
          is_staff: data.is_staff || false,
          // Role info from API
          role: roleObj.code || null,
          roleDetails: {
            id: roleObj.id,
            code: roleObj.code,
            name: roleObj.name
          },
          // Organization info from API
          organization: {
            id: orgObj.id,
            name: orgObj.name,
            code: orgObj.code
          },
          orgId: orgObj.id,
          employeeId: data.employee_id || null,
          // Permissions from API (e.g., ["rbacapp.hr_view", "rbacapp.hr_act"])
          permissions: data.permissions || [],
          must_reset_password: data.must_reset_password || false
        };
      }

      // Store organization ID separately for easy access
      if (userData.organization?.id) {
        localStorage.setItem('orgId', userData.organization.id);
      }

      // Store in state and localStorage
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      // Show success toast
      toast.success(`Welcome back, ${userData.first_name || userData.username}!`);
      if (userData.must_reset_password) {
        toast('For your security, please change your password as soon as possible.', { duration: 8000 });
      }

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      // The interceptor will already show a toast for 4xx/5xx errors
      // But we still need to return a failure object for the UI
      return {
        success: false,
        message: error.response?.data?.detail || error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  // Logout function — preserve theme prefs, clear everything else
  const logout = () => {
    setUser(null);
    queryClient.clear();
    const selectedOption = localStorage.getItem('selectedOption');
    const darkmodeState = localStorage.getItem('darkmodeState');
    const miniSidebarState = localStorage.getItem('miniSidebarState');
    localStorage.clear();
    if (selectedOption !== null) localStorage.setItem('selectedOption', selectedOption);
    if (darkmodeState !== null) localStorage.setItem('darkmodeState', darkmodeState);
    if (miniSidebarState !== null) localStorage.setItem('miniSidebarState', miniSidebarState);
  };

  // Check if user has a specific role code
  const hasRole = (roleCodes) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (Array.isArray(roleCodes)) {
      return roleCodes.includes(user.role);
    }
    return user.role === roleCodes;
  };

  // Get role display name from roleDetails
  const getRoleDisplayName = () => {
    if (!user) return '';
    if (user.is_superuser) return 'Super Admin';
    return user.roleDetails?.name || user.role || '';
  };

  // Check if user has a specific API permission (e.g., "rbacapp.hr_view")
  const hasApiPermission = (permission) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.permissions?.includes(permission) || false;
  };

  // Check if user has ANY of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => user.permissions?.includes(p));
  };

  // Check if user has ALL of the given permissions
  const hasAllPermissions = (permissions) => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(p => user.permissions?.includes(p));
  };

  // For super admin: allow setting organization context
  const setSelectedOrg = (orgId) => {
    if (!user) return;

    const updatedUser = {
      ...user,
      orgId: orgId
    };

    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    if (orgId) {
      localStorage.setItem('orgId', orgId);
    } else {
      localStorage.removeItem('orgId');
    }
  };

  const value = {
    user,
    login,
    logout,
    hasRole,
    getRoleDisplayName,
    // API permission helpers
    hasApiPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Super admin organization selector
    setSelectedOrg,
    isAuthenticated: !!user && (!!user.id || !!user.user_id),
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
